import { ReactNode } from 'react'
import { formatPoolFeeTierPercent } from 'utils/portfolio/poolDisplay'

import { PortfolioSchemaRow } from './PortfolioSchemaRow'
import { FormattedTokenBalance } from './TokenBalanceDisplay'
import { TokenPairLogo } from './TokenPairLogo'

interface PoolInfo {
  token0: {
    symbol: string
    logoUri?: string | null
  }
  token1: {
    symbol: string
    logoUri?: string | null
  }
  fee?: number
  address: string
}

interface PositionRowProps {
  pool: PoolInfo
  totalValue: number
  subRowCount: number
  children: ReactNode
  rangeDisplay?: ReactNode
  tokenBalancesDisplay?: { token0: string; token1: string }
  unclaimedFeesDisplay?: { token0: string; token1: string }
  badges?: ReactNode
}

export function PositionRow({
  pool,
  totalValue,
  subRowCount,
  children,
  rangeDisplay,
  tokenBalancesDisplay,
  unclaimedFeesDisplay,
  badges,
}: PositionRowProps) {
  const hasExpandableContent = subRowCount > 0
  const feeTierLabel = formatPoolFeeTierPercent(pool.fee)
  const formattedTotalValue = totalValue.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  return (
    <PortfolioSchemaRow
      hasExpandableContent={hasExpandableContent}
      containerClassName="sm:border-b sm:border-b-kodiak-border-secondary sm:last:border-b-0"
      mobileClassName="p-3 space-y-3"
      desktopClassName="items-center py-3 px-6 gap-4"
      desktopStyle={{
        gridTemplateColumns:
          'minmax(0, 1.55fr) minmax(0, 150px) minmax(0, 1fr) minmax(0, 0.6fr) minmax(0, 1fr) minmax(0, 0.8fr)',
      }}
      identity={{
        leading: <TokenPairLogo token0={pool.token0} token1={pool.token1} size="md" />,
        title: `${pool.token0.symbol}-${pool.token1.symbol}`,
        subtitle: (
          <>
            {feeTierLabel && `${feeTierLabel} • `}
            {subRowCount} position{subRowCount !== 1 ? 's' : ''}
          </>
        ),
        badges,
      }}
      mobile={{
        value: <div className="text-kodiak-text-primary font-medium text-sm sm:text-base">${formattedTotalValue}</div>,
        rightTop: badges,
        leftClassName: 'flex items-center space-x-3',
        leftTextClassName: 'flex flex-col gap-0.5',
        titleClassName: 'flex-col font-semibold text-kodiak-text-primary text-sm sm:text-base',
        subtitleClassName: 'text-xs text-kodiak-text-secondary',
      }}
      desktop={{
        showExpandIcon: true,
        containerClassName: 'flex-col items-center space-y-2 pr-3',
        rowClassName: 'flex items-center space-x-3 min-w-0',
        textClassName: 'flex flex-col gap-1',
        titleClassName:
          'flex items-center gap-2 font-medium text-kodiak-text-primary min-w-0 max-w-full overflow-hidden',
        titleTextClassName: 'flex-1 min-w-0 max-w-[160px] truncate',
        subtitleClassName: 'flex gap-1 flex-wrap text-xs text-kodiak-text-secondary',
        columns: (
          <>
            {rangeDisplay ? <div>{rangeDisplay}</div> : <div className="text-kodiak-text-secondary text-left">—</div>}

            {tokenBalancesDisplay ? (
              <div className="text-sm text-[#F8FAFC] space-y-1">
                <FormattedTokenBalance value={tokenBalancesDisplay.token0} />
                <FormattedTokenBalance value={tokenBalancesDisplay.token1} />
              </div>
            ) : (
              <div className="text-kodiak-text-secondary text-center">—</div>
            )}

            <div className="text-kodiak-text-primary font-medium">${formattedTotalValue}</div>

            {unclaimedFeesDisplay ? (
              <div className="text-sm text-[#6EE7B7] space-y-1">
                <div>{unclaimedFeesDisplay.token0}</div>
                <div>{unclaimedFeesDisplay.token1}</div>
              </div>
            ) : (
              <div className="text-kodiak-text-secondary text-left">—</div>
            )}

            <div className="text-center">—</div>
          </>
        ),
      }}
      renderExpanded={() => <div className="bg-kodiak-bg-module">{children}</div>}
    />
  )
}
